import translate, { hasTranslation } from '@translate';
import React, { FC } from 'react';
import { EditDataType } from 'src/utils/formFunctions';
import Tree, { CustomLabelElementType } from 'src/components/Tree';
import TextField from 'src/components/TextField';
import { styled } from '@mui/system';
import Select from 'src/components/Select';
import { SystemConfigStructureItemType } from 'src/types/systemConfigDetail';
import { ID_KEY } from '.';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Tooltip from 'src/components/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import EnabledShippersPicker from './EnabledShippersPicker';
import { ENABLED_SHIPPERS_ITEM_KEY } from './enabledShippersPicker';

const INPUT_FONT_SIZE = 17;

const InputStyled = styled('div')({
    position: 'absolute',
    right: '0px',
    top: '0px',

    '& .MuiFormControl-root.MuiTextField-root': {
        paddingTop: '0px',
        paddingBottom: '0px',

        '& > .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl': {
            padding: '0px',

            '& .MuiInputBase-input.MuiOutlinedInput-input.MuiInputBase-inputAdornedEnd': {
                padding: '0px 5px',
                height: `${INPUT_FONT_SIZE}px`,
            },

            '& .MuiButtonBase-root.MuiIconButton-root.MuiIconButton-sizeMedium.number-filter-clear': {
                '& > svg': {
                    width: `${INPUT_FONT_SIZE}px`,
                    height: `${INPUT_FONT_SIZE}px`,
                    fontSize: `${INPUT_FONT_SIZE}px`,
                },
            },
        },
    },
});

export type TreeOnChangeType = (
    structureItem: SystemConfigStructureItemType,
    value: string | number | undefined,
    error: boolean
) => void;

export interface TreeBlockPropsType {
    structureData?: SystemConfigStructureItemType[] | null;
    onChange: TreeOnChangeType;
    boolNumberCodes: ConstantsCodeType[];
    serviceCodes?: ConstantsCodeType[];
    editData: EditDataType;
    loading: boolean;
    permissionUpdate: boolean;
    setEditStructure: (structureItem: SystemConfigStructureItemType) => void;
}

const TreeBlock: FC<TreeBlockPropsType> = (props) => {
    if (!props.structureData) {
        return null;
    }

    const getInputElement = (structureItem: SystemConfigStructureItemType) => {
        const itemKey = structureItem[ID_KEY] || '';
        const value = props.editData[itemKey] || '';
        if (itemKey === ENABLED_SHIPPERS_ITEM_KEY) {
            return (
                <EnabledShippersPicker
                    value={typeof value === 'string' ? value : String(value ?? '')}
                    serviceCodes={props.serviceCodes ?? []}
                    onChange={(csv) => props.onChange(structureItem, csv, false)}
                    disabled={!props.permissionUpdate}
                />
            );
        }
        switch (structureItem.TYPE) {
            case 'boolean':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={value}
                        onChange={(nextValue, error) => {
                            const scalar = Array.isArray(nextValue) ? nextValue[0] : nextValue;
                            if (typeof scalar === 'boolean') {
                                props.onChange(structureItem, scalar ? 1 : 0, error);
                                return;
                            }
                            props.onChange(structureItem, scalar ?? '', error);
                        }}
                        name={itemKey}
                        disabled={!props.permissionUpdate}
                    />
                );
            case 'number':
                return (
                    <TextField
                        value={value}
                        onChange={(nextValue, error) => props.onChange(structureItem, nextValue, error)}
                        name={itemKey}
                        type="number"
                        disabled={!props.permissionUpdate}
                        forceFocus
                    />
                );
            case 'multiline':
                return (
                    <TextField
                        value={value}
                        onChange={(nextValue, error) => props.onChange(structureItem, nextValue, error)}
                        name={itemKey}
                        disabled={!props.permissionUpdate}
                        multiline
                        forceFocus
                    />
                );
            default:
                return (
                    <TextField
                        value={value}
                        onChange={(nextValue, error) => props.onChange(structureItem, nextValue, error)}
                        name={itemKey}
                        disabled={!props.permissionUpdate}
                        forceFocus
                    />
                );
        }
    };

    const handleClickEdit = (
        e: React.MouseEvent<SVGSVGElement, MouseEvent>,
        structureItem: SystemConfigStructureItemType
    ) => {
        e.preventDefault();
        e.stopPropagation();
        props.setEditStructure(structureItem);
    };

    const customLabelElement: CustomLabelElementType = (structureItem) => {
        return (
            <InputStyled>
                {getInputElement(structureItem)}
                <Tooltip title={translate('systemConfigDetail.editStructureItem')}>
                    <EditIcon fontSize="small" onClick={(e) => handleClickEdit(e, structureItem)} />
                </Tooltip>
                {structureItem.DESCRIPTION && (
                    <Tooltip title={structureItem.DESCRIPTION}>
                        <InfoIcon fontSize="small" />
                    </Tooltip>
                )}
            </InputStyled>
        );
    };

    const translateLabel = (keysArr: string[]): string => {
        const lastKey = keysArr[keysArr.length - 1];
        if (!lastKey) {
            return '';
        }
        const translationKey = `providerConfig.${lastKey}`;
        return hasTranslation(translationKey) ? translate(translationKey) : lastKey;
    };

    return (
        <Tree
            loading={!!props.loading}
            data={props.structureData}
            treeKey={ID_KEY}
            defaultTitle={translate('systemConfigDetail.titleTree')}
            customLabelElement={customLabelElement}
            translateLabel={translateLabel}
            highlightFocusLines
            sx={{ maxWidth: '700px' }}
        />
    );
};

export default TreeBlock;
